/* ============================================================
   VX CORE JS — Velaxen × Resilia rebuild
   Custom elements + VX namespace (money, cart).
   ES2020, zero dependencies, IIFE (module-safe), defensive:
   every customElements.define is guarded, missing slots never throw.
   ============================================================ */
(function () {
  'use strict';

  /* Marker class: gates the [data-vx-reveal] hidden state in vx-core.css */
  document.documentElement.classList.add('vx-js');

  /* ---------- helpers ---------- */

  function prefersReduced() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function debounce(fn, wait) {
    let t;
    return function () {
      const args = arguments;
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  }

  function pad2(n) {
    n = Math.max(0, Math.floor(n));
    return (n < 10 ? '0' : '') + n;
  }

  /* Run cb once when el scrolls into view (immediate fallback without IO). */
  function whenInView(el, cb, options) {
    if (!('IntersectionObserver' in window)) { cb(); return; }
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          io.disconnect();
          cb();
          break;
        }
      }
    }, options || { threshold: 0.3 });
    io.observe(el);
  }

  function storageGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function storageSet(key, value) {
    try { window.localStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }
  function sessionGet(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function sessionSet(key, value) {
    try { window.sessionStorage.setItem(key, value); } catch (e) { /* private mode */ }
  }

  function defineElement(name, ctor) {
    if (window.customElements && !customElements.get(name)) {
      customElements.define(name, ctor);
    }
  }

  /* ---------- window.VX namespace ---------- */

  const VX = (window.VX = window.VX || {});

  /**
   * VX.money(cents) — format a cent amount using the shop money format
   * when Shopify.formatMoney is available; otherwise "$X.XX".
   */
  VX.money = function (cents) {
    const amount = Number(cents);
    const safe = Number.isFinite(amount) ? amount : 0;
    const format =
      (window.theme && window.theme.moneyFormat) ||
      (window.Shopify && window.Shopify.money_format) ||
      null;
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(safe, format || undefined);
      } catch (e) { /* fall through to fallback */ }
    }
    return '$' + (safe / 100).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  /* ---------- VX.cart — AJAX cart helper ---------- */

  function cartRoute(key, fallback) {
    const routes = window.routes || {};
    let url = typeof routes[key] === 'string' && routes[key] ? routes[key] : fallback;
    return url.replace(/\.js$/, '') + '.js';
  }

  function cartFetch(url, options) {
    return fetch(url, options).then((res) => {
      if (!res.ok) {
        return res
          .json()
          .catch(() => ({}))
          .then((data) => {
            const msg =
              (data && (data.description || data.message)) ||
              'VX cart request failed (' + res.status + ')';
            throw new Error(msg);
          });
      }
      return res.json();
    });
  }

  VX.cart = {
    /** GET /cart.js → cart JSON */
    get() {
      return cartFetch(cartRoute('cart_url', '/cart'), {
        headers: { Accept: 'application/json' }
      });
    },

    /**
     * add(items[, options]) — items: a single {id, quantity, ...} or array.
     * options.sections: Section Rendering API param passthrough.
     * On success auto-dispatches vx:cart:refresh then vx:cart:open.
     */
    add(items, options) {
      let payload;
      if (Array.isArray(items)) payload = { items: items };
      else if (items && Array.isArray(items.items)) payload = items;
      else payload = { items: [items] };
      if (options && options.sections) payload.sections = options.sections;
      return cartFetch(cartRoute('cart_add_url', '/cart/add'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      }).then((data) => {
        VX.cart.refresh(data);
        VX.cart.open(data);
        return data;
      });
    },

    /**
     * change(key, qty[, options]) — change a line by line-item key (or variant id).
     * options.sections: Section Rendering API param passthrough.
     */
    change(key, qty, options) {
      const payload = { id: key, quantity: qty };
      if (options && options.sections) payload.sections = options.sections;
      return cartFetch(cartRoute('cart_change_url', '/cart/change'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
    },

    /** Dispatches vx:cart:open (cart drawer listens). */
    open(detail) {
      document.dispatchEvent(
        new CustomEvent('vx:cart:open', { bubbles: true, detail: detail || {} })
      );
    },

    /** Dispatches vx:cart:refresh (cart drawer / count badges listen). */
    refresh(detail) {
      document.dispatchEvent(
        new CustomEvent('vx:cart:refresh', { bubbles: true, detail: detail || {} })
      );
    }
  };

  /* ---------- <vx-countdown> ---------- */
  /*
    <vx-countdown data-mode="evergreen" data-hours="2" data-key="vx-sale">
      … [data-vx-h] [data-vx-m] [data-vx-s] …
    </vx-countdown>
    - evergreen: deadline = now + duration, persisted in localStorage[data-key];
      on expiry, resets to a fresh cycle.
    - evergreen + data-storage="session": deadline persisted in
      sessionStorage[data-key] so every browser session starts fresh; on
      expiry the timer strip (parent element) is hidden — no reset/loop.
    - fixed: data-deadline (ISO); on expiry hides the parent section.
    - data-minutes only (no data-hours) → M:SS mode (cart hold timer).
    - Missing slots: falls back to writing "HH:MM:SS" / "MM:SS" textContent.
  */
  class VxCountdown extends HTMLElement {
    connectedCallback() {
      if (this._timer) clearInterval(this._timer);

      this._mode = (this.dataset.mode || 'evergreen').toLowerCase();
      const hours = parseFloat(this.dataset.hours);
      const minutes = parseFloat(this.dataset.minutes);
      this._minutesOnly = !this.dataset.hours && !!this.dataset.minutes;

      let duration = 0;
      if (Number.isFinite(hours)) duration += hours * 3600000;
      if (Number.isFinite(minutes)) duration += minutes * 60000;
      if (duration <= 0) duration = 2 * 3600000;
      this._duration = duration;
      this._key = this.dataset.key || 'vx-countdown';

      this._slotH = this.querySelector('[data-vx-h]');
      this._slotM = this.querySelector('[data-vx-m]');
      this._slotS = this.querySelector('[data-vx-s]');

      this._session = (this.dataset.storage || '').toLowerCase() === 'session';
      this._lazy = (this.dataset.startOn || '').toLowerCase() === 'open';
      this._tick = this._tick.bind(this);

      if (this._mode === 'fixed') {
        const t = Date.parse(this.dataset.deadline || '');
        this._deadline = Number.isFinite(t) ? t : 0;
      } else if (this._session) {
        /* lazy mode (data-start-on="open"): do not seed or tick at connect —
           VX.startHold() arms the hold on first cart interaction. */
        if (!this._lazy) this._startSession();
        return;
      } else {
        this._deadline = this._evergreenDeadline();
      }

      this._tick();
      this._timer = setInterval(this._tick, 1000);
    }

    /* Session-hold start: seeds sessionStorage[key] = now + duration only if
       absent, then ticks. Safe to call repeatedly (idempotent resume). */
    _startSession() {
      const now = Date.now();
      let stored = parseInt(sessionGet(this._key), 10);
      if (!Number.isFinite(stored)) {
        stored = now + this._duration;
        sessionSet(this._key, String(stored));
      }
      if (stored <= now) {
        this._expireHide();
        return;
      }
      this._deadline = stored;
      if (this._timer) clearInterval(this._timer);
      this._tick();
      this._timer = setInterval(this._tick, 1000);
    }

    disconnectedCallback() {
      clearInterval(this._timer);
      this._timer = null;
    }

    _evergreenDeadline() {
      const now = Date.now();
      let stored = parseInt(storageGet(this._key), 10);
      if (!Number.isFinite(stored) || stored <= now) {
        stored = now + this._duration;
        storageSet(this._key, String(stored));
      }
      return stored;
    }

    _tick() {
      let remaining = this._deadline - Date.now();
      if (remaining <= 0) {
        if (this._mode === 'fixed') {
          clearInterval(this._timer);
          this._timer = null;
          this._hideSection();
          return;
        }
        if (this._session) {
          /* session mode: expire once, hide the strip, never reset */
          clearInterval(this._timer);
          this._timer = null;
          this._expireHide();
          return;
        }
        /* evergreen: fresh cycle */
        this._deadline = Date.now() + this._duration;
        storageSet(this._key, String(this._deadline));
        remaining = this._deadline - Date.now();
      }
      const total = Math.floor(remaining / 1000);

      if (this._minutesOnly) {
        const m = Math.floor(total / 60);
        const s = total % 60;
        if (this._slotM || this._slotS) {
          if (this._slotM) this._slotM.textContent = pad2(m);
          if (this._slotS) this._slotS.textContent = pad2(s);
        } else {
          this.textContent = pad2(m) + ':' + pad2(s);
        }
        return;
      }

      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      if (this._slotH || this._slotM || this._slotS) {
        if (this._slotH) this._slotH.textContent = pad2(h);
        if (this._slotM) this._slotM.textContent = pad2(m);
        if (this._slotS) this._slotS.textContent = pad2(s);
      } else {
        this.textContent = pad2(h) + ':' + pad2(m) + ':' + pad2(s);
      }
    }

    _hideSection() {
      const section =
        this.closest('.shopify-section') || this.closest('section') || this.parentElement;
      if (section) section.style.display = 'none';
    }

    /* session mode expiry: hide the immediate strip (parent element), not the
       whole section — e.g. the cart drawer's reservation bar. */
    _expireHide() {
      const strip = this.parentElement || this;
      strip.style.display = 'none';
    }
  }
  defineElement('vx-countdown', VxCountdown);

  /**
   * VX.startHold() — arm every lazy session countdown (data-start-on="open"
   * + data-storage="session"). Seeds the stored deadline only if absent, so
   * the hold always starts at full duration on first cart interaction and
   * resumes (or expire-hides) on later calls.
   */
  VX.startHold = function () {
    document.querySelectorAll('vx-countdown[data-start-on][data-storage]').forEach((el) => {
      if (el._lazy && el._session && typeof el._startSession === 'function') {
        el._startSession();
      }
    });
  };

  /* ---------- <vx-ticker> ---------- */
  /*
    <vx-ticker data-speed="40"><div class="vx-ticker__track">…items…</div></vx-ticker>
    Duplicates track content to fill ≥2× the host width, then loops
    translateX(-50%) via CSS keyframes (host gets .is-ready).
    data-speed = px per second (default 40). Reduced motion → static scroll.
  */
  class VxTicker extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      const run = () => requestAnimationFrame(() => this._setup());
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', run, { once: true });
      } else {
        run();
      }
    }

    _setup() {
      const track = this.querySelector('.vx-ticker__track');
      if (!track || !track.children.length) return;
      if (prefersReduced()) return; /* CSS shows a static scrollable strip */

      const unit = track.innerHTML;
      const minWidth = Math.max(this.clientWidth, window.innerWidth || 0);
      let guard = 0;
      while (track.scrollWidth < minWidth && guard++ < 10) {
        track.insertAdjacentHTML('beforeend', unit);
      }

      /* Append one aria-hidden duplicate of the full set → seamless -50% loop */
      const dupe = document.createElement('span');
      dupe.setAttribute('aria-hidden', 'true');
      dupe.style.display = 'contents';
      dupe.innerHTML = track.innerHTML;
      track.appendChild(dupe);

      this._applySpeed(track);
      this.classList.add('is-ready');

      this._onResize = debounce(() => this._applySpeed(track), 200);
      window.addEventListener('resize', this._onResize);
    }

    disconnectedCallback() {
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    _applySpeed(track) {
      const speed = parseFloat(this.dataset.speed) || 40; /* px per second */
      const half = track.scrollWidth / 2;
      if (half > 0) track.style.animationDuration = Math.max(8, half / speed) + 's';
    }
  }
  defineElement('vx-ticker', VxTicker);

  /* ---------- <vx-carousel> ---------- */
  /*
    <vx-carousel>
      <div class="vx-carousel__track"> …slides… </div>
      <button data-vx-prev>…</button> <button data-vx-next>…</button>
      <div data-vx-dots></div>
    </vx-carousel>
    Native scroll-snap; JS adds dots (one per slide), arrow paging, syncing.
  */
  class VxCarousel extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._track = this.querySelector('.vx-carousel__track');
      if (!this._track) return;
      this._prev = this.querySelector('[data-vx-prev]');
      this._next = this.querySelector('[data-vx-next]');
      this._dotsWrap = this.querySelector('[data-vx-dots]');
      this._dots = [];

      this._buildDots();
      if (this._prev) this._prev.addEventListener('click', () => this._step(-1));
      if (this._next) this._next.addEventListener('click', () => this._step(1));

      this._raf = null;
      this._track.addEventListener('scroll', () => {
        if (this._raf) return;
        this._raf = requestAnimationFrame(() => {
          this._raf = null;
          this._sync();
        });
      }, { passive: true });

      this._onResize = debounce(() => {
        this._buildDots();
        this._sync();
      }, 150);
      window.addEventListener('resize', this._onResize);

      this._sync();
    }

    disconnectedCallback() {
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    _slides() {
      return Array.prototype.slice.call(this._track.children).filter(
        (el) => !el.hasAttribute('aria-hidden')
      );
    }

    _index() {
      const slides = this._slides();
      const trackLeft = this._track.getBoundingClientRect().left;
      let best = 0;
      let bestDist = Infinity;
      slides.forEach((slide, i) => {
        const d = Math.abs(slide.getBoundingClientRect().left - trackLeft);
        if (d < bestDist) { bestDist = d; best = i; }
      });
      return best;
    }

    _scrollTo(i) {
      const slides = this._slides();
      const slide = slides[Math.max(0, Math.min(i, slides.length - 1))];
      if (!slide) return;
      const left =
        slide.getBoundingClientRect().left -
        this._track.getBoundingClientRect().left +
        this._track.scrollLeft;
      this._track.scrollTo({ left: left, behavior: prefersReduced() ? 'auto' : 'smooth' });
    }

    _step(dir) {
      this._scrollTo(this._index() + dir);
    }

    _buildDots() {
      if (!this._dotsWrap) return;
      const slides = this._slides();
      this._dotsWrap.innerHTML = '';
      this._dots = slides.map((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'vx-carousel__dot';
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.addEventListener('click', () => this._scrollTo(i));
        this._dotsWrap.appendChild(dot);
        return dot;
      });
    }

    _sync() {
      const track = this._track;
      const overflow = track.scrollWidth > track.clientWidth + 4;
      const i = this._index();
      this._dots.forEach((dot, di) => dot.classList.toggle('is-active', di === i));
      if (this._dotsWrap) this._dotsWrap.style.display = overflow ? '' : 'none';
      const atStart = track.scrollLeft <= 2;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      if (this._prev) this._prev.disabled = !overflow || atStart;
      if (this._next) this._next.disabled = !overflow || atEnd;
    }
  }
  defineElement('vx-carousel', VxCarousel);

  /* ---------- <vx-countup> ---------- */
  /*
    <vx-countup data-to="20" data-suffix="+" data-duration="1400"
                data-prefix="" data-decimals="0">0</vx-countup>
    Animates from 0 → data-to when scrolled into view (ease-out cubic).
  */
  class VxCountup extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this._to = parseFloat(this.dataset.to) || 0;
      this._duration = parseInt(this.dataset.duration, 10) || 1400;
      this._decimals = parseInt(this.dataset.decimals, 10) || 0;
      this._prefix = this.dataset.prefix || '';
      this._suffix = this.dataset.suffix || '';

      if (prefersReduced()) {
        this._render(this._to);
        return;
      }
      this._render(0);
      whenInView(this, () => this._animate(), { threshold: 0.4 });
    }

    _format(value) {
      return (
        this._prefix +
        value.toLocaleString('en-US', {
          minimumFractionDigits: this._decimals,
          maximumFractionDigits: this._decimals
        }) +
        this._suffix
      );
    }

    _render(value) {
      this.textContent = this._format(value);
    }

    _animate() {
      const start = performance.now();
      const dur = Math.max(1, this._duration);
      const to = this._to;
      const step = (now) => {
        const p = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - p, 3);
        this._render(this._decimals > 0 ? to * eased : Math.round(to * eased));
        if (p < 1) requestAnimationFrame(step);
        else this._render(to);
      };
      requestAnimationFrame(step);
    }
  }
  defineElement('vx-countup', VxCountup);

  /* ---------- <vx-accordion> ---------- */
  /*
    <vx-accordion>
      <details class="vx-accordion__item"><summary>…</summary>…</details> ×N
    </vx-accordion>
    Single-open; height animation via Web Animations API; reduced-motion safe.
  */
  class VxAccordion extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.addEventListener('click', (e) => {
        const summary = e.target.closest('summary');
        if (!summary || !this.contains(summary)) return;
        const details = summary.parentElement;
        if (!details || details.tagName !== 'DETAILS') return;
        e.preventDefault();
        if (details.open) {
          this._close(details);
        } else {
          this.querySelectorAll('details[open]').forEach((other) => {
            if (other !== details) this._close(other);
          });
          this._open(details);
        }
      });
    }

    _open(details) {
      this._cancel(details);
      const from = details.offsetHeight;
      details.open = true;
      const to = details.offsetHeight;
      this._animate(details, from, to, false);
    }

    _close(details) {
      this._cancel(details);
      const summary = details.querySelector('summary');
      const styles = window.getComputedStyle(details);
      const from = details.offsetHeight;
      const to =
        (summary ? summary.offsetHeight : 0) +
        (parseFloat(styles.borderTopWidth) || 0) +
        (parseFloat(styles.borderBottomWidth) || 0) +
        (parseFloat(styles.paddingTop) || 0) +
        (parseFloat(styles.paddingBottom) || 0);
      this._animate(details, from, to, true);
    }

    _cancel(details) {
      if (details._vxAnim) {
        details._vxAnim.cancel();
        details._vxAnim = null;
      }
    }

    _animate(details, from, to, closing) {
      if (prefersReduced() || typeof details.animate !== 'function') {
        if (closing) details.open = false;
        return;
      }
      details.style.overflow = 'hidden';
      const anim = details.animate(
        { height: [from + 'px', to + 'px'] },
        { duration: 300, easing: 'cubic-bezier(.4, 0, .2, 1)' }
      );
      details._vxAnim = anim;
      anim.onfinish = () => {
        details._vxAnim = null;
        details.style.overflow = '';
        if (closing) details.open = false;
      };
      anim.oncancel = () => {
        details._vxAnim = null;
        details.style.overflow = '';
      };
    }
  }
  defineElement('vx-accordion', VxAccordion);

  /* ---------- <vx-scarcity> ---------- */
  /*
    <vx-scarcity data-percent="93">
      <div class="vx-scarcity__track"><span class="vx-scarcity__fill"></span></div> …label…
    </vx-scarcity>
    Fills to data-percent (CSS 1s ease transition) once in view.
  */
  class VxScarcity extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      const fill = this.querySelector('.vx-scarcity__fill');
      if (!fill) return;
      const pct = Math.min(100, Math.max(0, parseFloat(this.dataset.percent) || 0));
      fill.style.width = '0%';
      whenInView(this, () => {
        requestAnimationFrame(() => { fill.style.width = pct + '%'; });
      }, { threshold: 0.3 });
    }
  }
  defineElement('vx-scarcity', VxScarcity);

  /* ---------- <vx-sticky-atc> ---------- */
  /*
    <vx-sticky-atc data-observe="#vx-buybox" [data-action="atc"]>…bar markup…</vx-sticky-atc>
    Slides up (.is-visible) once the observed element has scrolled above the
    viewport. Button click: data-action="atc" → submits the product form;
    otherwise smooth-scrolls back to the observed element.
  */
  class VxStickyAtc extends HTMLElement {
    connectedCallback() {
      const selector = this.dataset.observe;
      this._target = selector ? document.querySelector(selector) : null;

      if (this._io) this._io.disconnect();
      if (this._target && 'IntersectionObserver' in window) {
        this._io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const passed = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
            this.classList.toggle('is-visible', passed);
          });
        }, { threshold: 0 });
        this._io.observe(this._target);
      }

      if (!this._bound) {
        this._bound = true;
        this.addEventListener('click', (e) => {
          const control = e.target.closest('a, button');
          if (!control || !this.contains(control)) return;
          if ((this.dataset.action || '').toLowerCase() === 'atc') {
            const scope = this._target || document;
            const form =
              scope.querySelector('form.vx-product-form') ||
              scope.querySelector('form[action*="/cart/add"]') ||
              document.querySelector('form.vx-product-form') ||
              document.querySelector('form[action*="/cart/add"]');
            if (form) {
              e.preventDefault();
              if (typeof form.requestSubmit === 'function') form.requestSubmit();
              else form.submit();
              return;
            }
          }
          if (this._target) {
            e.preventDefault();
            this._target.scrollIntoView({
              behavior: prefersReduced() ? 'auto' : 'smooth',
              block: 'start'
            });
          }
        });
      }
    }

    disconnectedCallback() {
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
    }
  }
  defineElement('vx-sticky-atc', VxStickyAtc);

  /* ---------- reveal-on-scroll ([data-vx-reveal]) ---------- */
  function initReveal(root) {
    const scope = root && root.querySelectorAll ? root : document;
    let els = Array.from(scope.querySelectorAll('[data-vx-reveal]:not(.is-in)'));
    if (!els.length) return;

    if (!('IntersectionObserver' in window) || prefersReduced()) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }

    /* Elements already inside the initial viewport reveal immediately —
       above-the-fold content must never wait for the observer. */
    const vh = window.innerHeight || document.documentElement.clientHeight;
    els = els.filter((el) => {
      const r = el.getBoundingClientRect();
      if (r.top < vh + 60 && r.bottom > -60) {
        el.classList.add('is-in');
        return false;
      }
      return true;
    });
    if (!els.length) return;

    const io = new IntersectionObserver((entries) => {
      /* group entering elements by parent so stagger is per-parent */
      const byParent = new Map();
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        const parent = entry.target.parentElement || document.body;
        if (!byParent.has(parent)) byParent.set(parent, []);
        byParent.get(parent).push(entry.target);
      });
      byParent.forEach((group) => {
        group.sort((a, b) =>
          a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
        );
        group.forEach((el, i) => {
          el.style.transitionDelay = i * 60 + 'ms';
          el.classList.add('is-in');
          el.addEventListener('transitionend', function clearDelay() {
            el.style.transitionDelay = '';
          }, { once: true });
        });
      });
    }, { threshold: 0.06, rootMargin: '0px 0px 120px 0px' });

    els.forEach((el) => io.observe(el));
  }

  /* ---------- delivery-date fill ([data-vx-delivery]) ---------- */
  /* Fills with now + N days (default 4), skipping Sundays.
     Override per element: data-vx-delivery="5". */
  function fillDelivery(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll('[data-vx-delivery]').forEach((el) => {
      let days = parseInt(el.getAttribute('data-vx-delivery'), 10);
      if (!Number.isFinite(days) || days <= 0) days = 4;
      const date = new Date();
      let added = 0;
      while (added < days) {
        date.setDate(date.getDate() + 1);
        if (date.getDay() !== 0) added++;
      }
      el.textContent = date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
      });
    });
  }

  /* ---------- footnote marks (†, ‡, trailing *) ----------
     Renders disclaimers as small superscripts site-wide instead of
     full-size glyphs. Text-node walk over vx- sections only. */
  function shrinkFootnotes(root) {
    const scope = root && root.querySelectorAll ? root : document;
    const hosts = scope.querySelectorAll('[class*="vx-"]');
    const seen = new Set();
    hosts.forEach((host) => {
      if (host.closest('script, style, textarea')) return;
      const walker = document.createTreeWalker(host, NodeFilter.SHOW_TEXT, {
        acceptNode(n) {
          if (!n.nodeValue || !/[†‡]|\*(?=\s|$|[,.)"”])/.test(n.nodeValue)) return NodeFilter.FILTER_REJECT;
          const p = n.parentElement;
          if (!p || seen.has(n) || p.closest('sup, script, style') || p.classList.contains('vx-fn')) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      nodes.forEach((n) => {
        seen.add(n);
        const frag = document.createDocumentFragment();
        const parts = n.nodeValue.split(/([†‡]|\*(?=\s|$|[,.)"”]))/);
        parts.forEach((part) => {
          if (part === '†' || part === '‡' || part === '*') {
            const sup = document.createElement('sup');
            sup.className = 'vx-fn';
            sup.textContent = part;
            frag.appendChild(sup);
          } else if (part) {
            frag.appendChild(document.createTextNode(part));
          }
        });
        n.parentNode.replaceChild(frag, n);
      });
    });
  }

  /* Re-runnable on any subtree (idempotent) — cart drawer calls this after
     AJAX re-renders so injected daggers shrink too. */
  window.vxShrinkFootnotes = shrinkFootnotes;

  /* ---------- boot ---------- */
  /* Shrink immediately at evaluation (deferred = DOM already parsed) so
     daggers never paint full-size while waiting for DOMContentLoaded. */
  shrinkFootnotes(document);

  onReady(() => {
    initReveal(document);
    fillDelivery(document);
    shrinkFootnotes(document);
  });

  /* Theme editor: re-scan re-rendered sections */
  document.addEventListener('shopify:section:load', (e) => {
    initReveal(e.target || document);
    fillDelivery(e.target || document);
    shrinkFootnotes(e.target || document);
  });
})();
